import { RoomPriceData, Insight } from './PricePerRoomChart.types'
import {
  formatPrice as formatCentralizedPrice,
  formatShortPrice as formatCentralizedShortPrice,
} from '../../../utils/number/formatUtils'

export const formatPrice = (
  price: number,
  isRental: boolean = false
): string => {
  return formatCentralizedPrice(price, isRental)
}

export const formatShortPrice = (
  price: number,
  isRental: boolean = false
): string => {
  return formatCentralizedShortPrice(price, isRental)
}

export const processRoomData = (
  aggregations: any,
  _priceType: 'median' | 'average' = 'median'
): RoomPriceData[] => {
  if (!aggregations?.by_category?.buckets) {
    return []
  }

  const processed: RoomPriceData[] = []

  aggregations.by_category.buckets.forEach((categoryBucket: any) => {
    const category = categoryBucket.key

    categoryBucket.by_rooms?.buckets?.forEach((bucket: any) => {
      const rooms = bucket.key

      // Filter out outliers and invalid data
      if (rooms > 0 && rooms <= 10 && bucket.listing_count?.value > 2) {
        const dataPoint: any = {
          category,
          rooms,
          avgPrice: Math.round(bucket.avg_price?.value || 0),
          medianPrice: Math.round(
            bucket.median_price?.values?.['50.0'] ||
              bucket.median_price?.values?.['50'] ||
              0
          ),
          count: bucket.listing_count.value,
          stdDev: Math.round(bucket.price_stats?.std_deviation || 0),
          min: Math.round(bucket.price_stats?.min || 0),
          max: Math.round(bucket.price_stats?.max || 0),
        }

        // Add quantiles if available - check multiple possible formats
        if (bucket.quantiles?.values) {
          dataPoint.quantiles = {
            q10: Math.round(
              bucket.quantiles.values['10.0'] ||
                bucket.quantiles.values['10'] ||
                0
            ),
            q30: Math.round(
              bucket.quantiles.values['30.0'] ||
                bucket.quantiles.values['30'] ||
                0
            ),
            q50: Math.round(
              bucket.quantiles.values['50.0'] ||
                bucket.quantiles.values['50'] ||
                0
            ),
            q70: Math.round(
              bucket.quantiles.values['70.0'] ||
                bucket.quantiles.values['70'] ||
                0
            ),
            q90: Math.round(
              bucket.quantiles.values['90.0'] ||
                bucket.quantiles.values['90'] ||
                0
            ),
          }
        }

        processed.push(dataPoint)
      }
    })
  })

  return processed
}

export const calculateKPIs = (
  data: RoomPriceData[],
  priceType: 'median' | 'average' = 'median'
) => {
  if (data.length === 0) {
    return {
      totalListings: 0,
      avgPrice: 0,
      avgRooms: 0,
      avgPricePerRoom: 0,
    }
  }

  const totalListings = data.reduce((sum, d) => sum + d.count, 0)
  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
  const weightedPrice =
    data.reduce((sum, d) => sum + d[priceField] * d.count, 0) / totalListings
  const avgRooms =
    data.reduce((sum, d) => sum + d.rooms * d.count, 0) / totalListings
  const avgPricePerRoom = weightedPrice / avgRooms

  return {
    totalListings,
    avgPrice: weightedPrice,
    avgRooms,
    avgPricePerRoom,
  }
}

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  data: RoomPriceData[],
  priceType: 'median' | 'average' = 'median',
  isRental: boolean = false,
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  if (data.length === 0) {
    return insights
  }

  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

  // Best value analysis
  const valueRatios = data.map((d) => ({
    ...d,
    pricePerRoom: d[priceField] / d.rooms,
  }))

  const bestValue = valueRatios.reduce((best, current) =>
    current.pricePerRoom < best.pricePerRoom ? current : best
  )

  insights.push({
    title: t('charts.insights.bestValue'),
    description: t('charts.insights.descriptions.pricePerRoom.bestValue', {
      rooms: bestValue.rooms,
      category: bestValue.category.toLowerCase(),
      price: formatPrice(bestValue.pricePerRoom, isRental),
    }),
  })

  // Price variation analysis
  const apartmentData = data.filter((d) => d.category === 'Apartment')
  const houseData = data.filter((d) => d.category === 'House')

  if (apartmentData.length > 0 && houseData.length > 0) {
    const avgApartmentPrice =
      apartmentData.reduce((sum, d) => sum + d[priceField] * d.count, 0) /
      apartmentData.reduce((sum, d) => sum + d.count, 0)
    const avgHousePrice =
      houseData.reduce((sum, d) => sum + d[priceField] * d.count, 0) /
      houseData.reduce((sum, d) => sum + d.count, 0)
    const priceDiff =
      ((avgHousePrice - avgApartmentPrice) / avgApartmentPrice) * 100

    insights.push({
      title: t('charts.insights.propertyTypePremium'),
      description: t(
        'charts.insights.descriptions.pricePerRoom.propertyTypePremium',
        {
          premium: Math.abs(priceDiff).toFixed(1),
          comparison:
            priceDiff >= 0
              ? t('charts.common.moreExpensive') || 'more expensive'
              : t('charts.common.lessExpensive') || 'less expensive',
        }
      ),
    })
  }

  // Market sweet spot
  const totalListings = data.reduce((sum, d) => sum + d.count, 0)
  const popularConfigs = data.filter((d) => d.count > totalListings * 0.05)

  if (popularConfigs.length > 0) {
    const mostPopular = popularConfigs.reduce((max, current) =>
      current.count > max.count ? current : max
    )

    const percentage = ((mostPopular.count / totalListings) * 100).toFixed(1)
    insights.push({
      title: t('charts.insights.marketSweetSpot'),
      description: t(
        'charts.insights.descriptions.pricePerRoom.marketSweetSpot',
        {
          rooms: mostPopular.rooms,
          category: mostPopular.category.toLowerCase(),
          count: mostPopular.count.toLocaleString(),
          percentage,
        }
      ),
    })
  }

  // Price progression analysis
  const roomCategories = [...new Set(data.map((d) => d.category))]
  roomCategories.forEach((category) => {
    const categoryData = data
      .filter((d) => d.category === category)
      .sort((a, b) => a.rooms - b.rooms)
    if (categoryData.length >= 3) {
      const priceIncreases = []
      for (let i = 1; i < categoryData.length; i++) {
        const increase =
          ((categoryData[i][priceField] - categoryData[i - 1][priceField]) /
            categoryData[i - 1][priceField]) *
          100
        priceIncreases.push(increase)
      }
      const avgIncrease =
        priceIncreases.reduce((a, b) => a + b, 0) / priceIncreases.length

      const pattern =
        avgIncrease > 15
          ? t('charts.common.steep') || 'steep'
          : avgIncrease > 5
            ? t('charts.common.moderate') || 'moderate'
            : t('charts.common.gradual') || 'gradual'
      insights.push({
        title: t('charts.insights.priceProgression', { category }),
        description: t(
          'charts.insights.descriptions.pricePerRoom.priceProgression',
          {
            category: category.toLowerCase(),
            increment: avgIncrease.toFixed(1) + '%',
            pattern,
          }
        ),
      })
    }
  })

  return insights
}

export const calculateTrendLine = (
  data: RoomPriceData[],
  priceType: 'median' | 'average' = 'median'
) => {
  if (data.length < 2) return null

  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
  const n = data.length
  const sumX = data.reduce((sum, d) => sum + d.rooms, 0)
  const sumY = data.reduce((sum, d) => sum + d[priceField], 0)
  const sumXY = data.reduce((sum, d) => sum + d.rooms * d[priceField], 0)
  const sumX2 = data.reduce((sum, d) => sum + d.rooms * d.rooms, 0)

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  const minX = Math.min(...data.map((d) => d.rooms))
  const maxX = Math.max(...data.map((d) => d.rooms))

  return [
    [minX, slope * minX + intercept],
    [maxX, slope * maxX + intercept],
  ]
}

export const getChartColors = () => ({
  apartment: '#2563eb',
  house: '#10b981',
})
